/**
 * Scout v0 — post-launch result summary (P16).
 *
 * After a launch, show the recorded run identity (run id + criteria version),
 * the funnel counts, and the graded leads the pipeline produced. This is a
 * read-only summary of the LaunchResponse — the run is already recorded.
 */

import { Link } from '@tanstack/react-router';
import type { LaunchResponse } from '@/types/scout';

interface LaunchResultProps {
  result: LaunchResponse;
  /** Reset the composer to start a fresh run. */
  onComposeAnother: () => void;
}

const COUNT_LABELS: { key: keyof LaunchResponse['counts']; label: string }[] = [
  { key: 'rawItems', label: 'Raw items' },
  { key: 'gated', label: 'Gated' },
  { key: 'routedEligible', label: 'Routed eligible' },
  { key: 'qualifiedJobs', label: 'Qualified jobs' },
  { key: 'keptJobs', label: 'Kept jobs' },
  { key: 'usableLeads', label: 'Usable leads' },
  { key: 'contactRouted', label: 'Contact routed' },
];

export function LaunchResult({ result, onComposeAnother }: LaunchResultProps) {
  return (
    <section className="card" aria-labelledby="result-heading">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 id="result-heading" className="text-lg font-semibold text-text">
            Run launched
          </h2>
          <p className="text-xs text-muted mt-0.5 font-mono">
            {result.runId} · {result.criteriaVersion}
          </p>
        </div>
        <span className="badge badge-primary">recorded</span>
      </div>

      {/* Funnel counts */}
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {COUNT_LABELS.map(({ key, label }) => (
          <div key={key} className="border border-border rounded-md p-2.5">
            <dt className="text-xs text-muted">{label}</dt>
            <dd className="text-lg font-semibold text-text">{result.counts[key]}</dd>
          </div>
        ))}
      </dl>

      {/* Leads */}
      {result.leads.length === 0 ? (
        <p className="text-sm text-muted italic">No usable leads produced by this run.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-border">
                <th className="py-2 pr-3 font-medium">Company</th>
                <th className="py-2 pr-3 font-medium">Role</th>
                <th className="py-2 pr-3 font-medium">Grade</th>
                <th className="py-2 pr-3 font-medium">Rank</th>
                <th className="py-2 pr-3 font-medium">Size</th>
                <th className="py-2 pr-3 font-medium">Route</th>
              </tr>
            </thead>
            <tbody>
              {result.leads.map((lead) => (
                <tr key={lead.rowRef} className="border-b border-border/50">
                  <td className="py-2 pr-3 text-text">{lead.company}</td>
                  <td className="py-2 pr-3 text-muted">{lead.jobTitle}</td>
                  <td className="py-2 pr-3">
                    <span className={`badge ${lead.usable ? 'badge-primary' : 'badge-warning'}`}>
                      {lead.grade}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-muted">{lead.rank}</td>
                  <td className="py-2 pr-3 text-muted">{lead.sizeBand}</td>
                  <td className="py-2 pr-3 text-muted">{lead.routeStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end gap-3 mt-5">
        <button type="button" className="btn-secondary" onClick={onComposeAnother}>
          Compose another run
        </button>
        <Link to="/scout/runs/$runId" params={{ runId: result.runId }} className="btn-primary">
          View run status
        </Link>
      </div>
    </section>
  );
}
